import { useEffect, useState } from "react";

type PublicWelcomeScreenProps = {
  onLogin: () => void;
};

export default function PublicWelcomeScreen({ onLogin }: PublicWelcomeScreenProps) {
  const [videoAvailable, setVideoAvailable] = useState(true);
  const [showScrollCue, setShowScrollCue] = useState(true);

  useEffect(() => {
    const updateScrollCue = () => setShowScrollCue(window.scrollY < 160);
    updateScrollCue();
    window.addEventListener("scroll", updateScrollCue, { passive: true });
    return () => window.removeEventListener("scroll", updateScrollCue);
  }, []);

  return (
    <main className="public-home">
      <header className="public-header">
        <a className="public-brand" href="/" aria-label="Calm But Capable home">
          <img src="/logo-white.png" alt="" className="public-logo" />
          <span>Calm But Capable</span>
        </a>

        <button className="public-login" type="button" onClick={onLogin}>
          Login
        </button>
      </header>

      <section className="public-intro" aria-labelledby="welcome-heading">
        <p className="public-kicker">Understanding before judgement</p>
        <h1 id="welcome-heading">Welcome to Calm But Capable</h1>

        <p className="public-intro-copy">
          Begin with a free practical workshop, explore the ideas through the
          book, or join Sanctuary for continuing tools and support. Start at the
          stage that is right for you.
        </p>

        <div className="public-intro-actions">
          <a
            className="public-primary-action"
            href="https://calm-but-capable.kit.com/3e9b23e333"
          >
            Get the free workshop <span aria-hidden="true">→</span>
          </a>
          <a className="public-secondary-action" href="#individual-pathway">
            See all ways to begin
          </a>
        </div>

        <div className="public-video-shell">
          {videoAvailable ? (
            <video
              className="public-video"
              autoPlay
              muted
              playsInline
              controls
              preload="metadata"
              poster="/welcome-cbc-poster.jpg"
              onError={() => setVideoAvailable(false)}
            >
              <source src="/welcome-cbc.mp4" type="video/mp4" />
              <track
                default
                kind="captions"
                src="/welcome-cbc.vtt"
                srcLang="en"
                label="English"
              />
            </video>
          ) : (
            <div className="public-video-placeholder">
              <span className="public-play-mark" aria-hidden="true">▶</span>
              <p>Your Calm But Capable welcome video will appear here.</p>
            </div>
          )}
        </div>
      </section>

      {showScrollCue && (
        <a
          className="public-scroll-cue"
          href="#individual-pathway"
          onClick={() => setShowScrollCue(false)}
        >
          <span>Choose where to begin</span>
          <span className="public-scroll-arrow" aria-hidden="true">↓</span>
        </a>
      )}

      <section
        className="public-pathway"
        id="individual-pathway"
        aria-labelledby="pathway-heading"
      >
        <div className="public-routes-heading">
          <p className="public-kicker">Your journey, your starting point</p>
          <h2 id="pathway-heading">Choose where you would like to begin</h2>
          <p>
            These options form a natural pathway, but not a set of gates. You
            can begin at any stage without completing the one before it.
          </p>
        </div>

        <div className="public-pathway-grid">
          <article className="public-pathway-card public-pathway-card--workshop">
            <span className="public-pathway-number">01</span>
            <p className="public-card-label">Try the approach</p>
            <h3>Free Workshop</h3>
            <p>
              Slow the noise down, identify what matters next and take one
              clear, manageable step.
            </p>
            <a
              className="public-route-link"
              href="https://calm-but-capable.kit.com/3e9b23e333"
            >
              Start the free workshop <span aria-hidden="true">→</span>
            </a>
          </article>

          <article className="public-pathway-card public-pathway-card--book">
            <span className="public-pathway-number">02</span>
            <p className="public-card-label">Understand and explore</p>
            <h3>Calm But Capable</h3>
            <p>
              Use the book to understand what may be getting in your way and
              begin building your personal Toolkit.
            </p>
            <p className="public-pathway-price">£5.99 <span>UK delivery included</span></p>
            <a className="public-route-link" href="/individuals">
              Explore the book <span aria-hidden="true">→</span>
            </a>
          </article>

          <article className="public-pathway-card public-pathway-card--sanctuary">
            <span className="public-pathway-number">03</span>
            <p className="public-card-label">Continue moving forward</p>
            <h3>Sanctuary Membership</h3>
            <p>
              Build continuing momentum through practical tools, education and
              support that adapts around you.
            </p>
            <p className="public-pathway-price">£30 <span>per month</span></p>
            <a className="public-route-link" href="/sanctuary">
              Explore Sanctuary <span aria-hidden="true">→</span>
            </a>
          </article>
        </div>

        <p className="public-pathway-note">
          Prefer to commit for a year? Annual membership is £299, with a
          limited first-50 founding member offer.
        </p>
      </section>

      <section className="public-routes public-routes--organisations" aria-labelledby="route-heading">
        <div className="public-routes-heading">
          <p className="public-kicker">For teams, education and communities</p>
          <h2 id="route-heading">Looking for organisational support?</h2>
        </div>

        <div className="public-route-grid public-route-grid--single">
          <article className="public-route-card public-route-card--organisations">
            <p className="public-card-label">For teams and communities</p>
            <h3>Organisations and Education</h3>
            <p>
              Help students, staff or the people you support recognise their
              capability and take achievable steps towards progress.
            </p>
            <a className="public-route-link" href="/organisations">
              Discover how we can help <span aria-hidden="true">→</span>
            </a>
          </article>
        </div>
      </section>

      <section className="public-belief">
        <p>
          People are often more capable than they appear—and more capable than
          they realise.
        </p>
      </section>
    </main>
  );
}
